"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

type FeatureAccordionProps = {
	features: Array<
		| string
		| null
		| {
				feature?: string | null;
				description?: string | null;
		  }
	>;
};

const FeatureAccordion = ({ features }: FeatureAccordionProps) => {
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	const toggleIndex = (index: number) => {
		setOpenIndex((current) => (current === index ? null : index));
	};

	return (
		<div className="flex flex-col gap-y-1">
			{features?.filter(Boolean).map((item, index) => {
				const title = typeof item === "string" ? item : (item?.feature ?? "");
				const rawDescription = typeof item === "string" ? "" : (item?.description ?? "");
				const description = (rawDescription || "").trim();
				const canExpand = description.length > 0;

				return (
					<div
						key={`feature-${index}`}
						className="group rounded-[0.625rem] border border-gray-300 font-secondary text-[#1A1A1A] transition-colors duration-500 hover:border-blue"
					>
						<button
							type="button"
							aria-expanded={canExpand ? openIndex === index : undefined}
							aria-disabled={!canExpand}
							onClick={canExpand ? () => toggleIndex(index) : undefined}
							className={`flex w-full items-center justify-between gap-4 p-5 text-left font-secondary-semibold transition-colors duration-500 ${
								canExpand ? "cursor-pointer" : "cursor-default"
							}`}
						>
							<span className="transition-colors duration-500 group-hover:text-blue">{title}</span>
							{canExpand && (
								<span
									className={`inline-flex h-5 w-5 shrink-0 items-center justify-center text-blue transition-transform duration-300 ${
										openIndex === index ? "rotate-180" : "rotate-0"
									}`}
									aria-hidden="true"
								>
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</span>
							)}
						</button>
						{canExpand && (
							<AnimatePresence initial={false}>
								{openIndex === index && (
									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: "auto", opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.25, ease: "easeInOut" }}
										className="overflow-hidden"
									>
										<div className="px-5 pb-5 text-gray-700">{description}</div>
									</motion.div>
								)}
							</AnimatePresence>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default FeatureAccordion;
